import { type ReactElement } from 'react';
import { AlertTriangle, CheckCircle2, Copy, MoreHorizontal, RotateCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DataTableHeaderRow,
  DataTableTable,
  DataTableTbody,
  DataTableTd,
  DataTableTh,
  DataTableThead,
  DataTableTr,
} from '@/components/ui/data-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import type { TenantViewModel } from '../api';
import { DeploymentStatusPill } from './DeploymentStatusPill';

function truncateUuid(uuid: string): string {
  return uuid.length > 13 ? `${uuid.slice(0, 8)}…${uuid.slice(-4)}` : uuid;
}

export function TenantsTable({
  tenants,
  seedEnabled,
  showOverrides,
  onView,
  onEdit,
  onReinit,
  onSeed,
  onCopyUuid,
}: {
  readonly tenants: readonly TenantViewModel[];
  readonly seedEnabled: boolean;
  /** Whether to show the Overrides column (hidden when DM is unreachable). */
  readonly showOverrides: boolean;
  readonly onView: (t: TenantViewModel) => void;
  readonly onEdit: (t: TenantViewModel) => void;
  readonly onReinit: (t: TenantViewModel) => void;
  readonly onSeed: (t: TenantViewModel) => void;
  readonly onCopyUuid: (t: TenantViewModel) => void;
}): ReactElement {
  const { t } = useTranslation();

  return (
    <DataTableTable aria-label={t('routes.platform.tenants.listAriaLabel')}>
      <DataTableThead className="sticky top-0 z-10 bg-card">
        <DataTableHeaderRow>
          <DataTableTh widthClassName="w-[22%] min-w-[8rem]">
            {t('routes.platform.tenants.table.label')}
          </DataTableTh>
          <DataTableTh widthClassName="w-[26%] min-w-[10rem]">
            {t('routes.platform.tenants.table.name')}
          </DataTableTh>
          <DataTableTh widthClassName="w-[10%] min-w-[5rem]">
            {t('routes.platform.tenants.table.state')}
          </DataTableTh>
          <DataTableTh widthClassName="w-[14%] min-w-[6rem]">
            {t('routes.platform.tenants.table.init')}
          </DataTableTh>
          {showOverrides && (
            <DataTableTh widthClassName="w-[18%] min-w-[8rem]">
              {t('routes.platform.tenants.table.overrides')}
            </DataTableTh>
          )}
          <DataTableTh widthClassName="w-[20%] min-w-[8rem]">
            {t('routes.platform.tenants.table.uuid')}
          </DataTableTh>
          <DataTableTh widthClassName="w-[8%]" align="right">
            <span className="sr-only">{t('routes.platform.tenants.table.actions')}</span>
          </DataTableTh>
        </DataTableHeaderRow>
      </DataTableThead>
      <DataTableTbody>
        {tenants.map((tenant) => (
          <DataTableTr key={tenant.uuid} className="even:bg-muted/40">
            <DataTableTd className="font-medium text-foreground">
              <button
                type="button"
                onClick={() => { onView(tenant); }}
                className="text-left hover:underline"
              >
                {tenant.label.length > 0 ? tenant.label : tenant.name}
              </button>
            </DataTableTd>
            <DataTableTd dataOffset>
              <div className="flex flex-col">
                <span className="font-mono text-[12px]" title={t('routes.platform.tenants.schemaTooltip')}>
                  {tenant.name}
                </span>
                <span className="font-mono text-[10.5px] text-muted-foreground">
                  {tenant.schemaName}
                </span>
              </div>
            </DataTableTd>
            <DataTableTd dataOffset>
              <Badge variant={tenant.state === 'active' ? 'secondary' : 'outline'}>
                {t(`routes.platform.tenants.state.${tenant.state}`)}
              </Badge>
            </DataTableTd>
            <DataTableTd dataOffset>
              {tenant.schemaInitialized ? (
                <span className="inline-flex items-center gap-1 text-[12px] text-emerald-700">
                  <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('routes.platform.tenants.init.ok')}
                </span>
              ) : (
                <span className="inline-flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 text-[12px] text-amber-700">
                    <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('routes.platform.tenants.init.partial')}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-6 px-2 text-[11px]"
                    onClick={() => { onReinit(tenant); }}
                  >
                    <RotateCw className="mr-1 h-3 w-3" aria-hidden="true" />
                    {t('routes.platform.tenants.init.reinit')}
                  </Button>
                </span>
              )}
            </DataTableTd>
            {showOverrides && (
              <DataTableTd dataOffset>
                {tenant.hasOverrides ? (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge variant="outline" className="font-mono text-[10.5px]">
                      {tenant.serviceOverrides.map((o) => o.serviceName).join(', ')}
                    </Badge>
                    <DeploymentStatusPill status={tenant.deploymentStatus} />
                  </div>
                ) : (
                  <span className="text-[12px] text-muted-foreground">
                    {t('routes.platform.tenants.overridesColumn.none')}
                  </span>
                )}
              </DataTableTd>
            )}
            <DataTableTd dataOffset>
              <button
                type="button"
                onClick={() => { onCopyUuid(tenant); }}
                title={t('routes.platform.tenants.copyUuid')}
                aria-label={t('routes.platform.tenants.copyUuid')}
                className="inline-flex items-center gap-1 font-mono text-[11px] text-muted-foreground hover:text-foreground"
              >
                {truncateUuid(tenant.uuid)}
                <Copy className="h-3 w-3" aria-hidden="true" />
              </button>
            </DataTableTd>
            <DataTableTd align="right" dataOffset>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground"
                    aria-label={t('routes.platform.tenants.rowMenu.open')}
                  >
                    <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => { onView(tenant); }}>
                    {t('routes.platform.tenants.rowMenu.view')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => { onEdit(tenant); }}>
                    {t('routes.platform.tenants.rowMenu.edit')}
                  </DropdownMenuItem>
                  {!tenant.schemaInitialized && (
                    <DropdownMenuItem onSelect={() => { onReinit(tenant); }}>
                      {t('routes.platform.tenants.rowMenu.reinit')}
                    </DropdownMenuItem>
                  )}
                  {seedEnabled && (
                    <DropdownMenuItem onSelect={() => { onSeed(tenant); }}>
                      {t('routes.platform.tenants.rowMenu.seed')}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </DataTableTd>
          </DataTableTr>
        ))}
      </DataTableTbody>
    </DataTableTable>
  );
}
